import { useState } from "react";
import { Calendar, Download, ExternalLink, ChevronDown } from "lucide-react";
import { Button } from "./ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "./ui/dropdown-menu";

/**
 * AddToCalendar component - No OAuth required!
 * Generates direct links to add events to Google Calendar, Apple Calendar, and Outlook
 */
const AddToCalendar = ({ 
  title, 
  description, 
  location, 
  startDate, // "YYYY-MM-DD"
  startTime, // "HH:MM" (24h format)
  endTime,   // "HH:MM" (24h format)
  className = "",
  variant = "outline",
  size = "sm"
}) => {
  const [downloading, setDownloading] = useState(false);

  // Format date/time for Google Calendar URL (YYYYMMDDTHHmmss)
  const formatGoogleDateTime = (date, time) => {
    if (!date) return "";
    const dateStr = date.replace(/-/g, "");
    const timeStr = time ? time.replace(":", "") + "00" : "000000";
    return `${dateStr}T${timeStr}`;
  };

  // Generate Google Calendar URL
  const getGoogleCalendarUrl = () => {
    const params = new URLSearchParams();
    params.set("action", "TEMPLATE");
    params.set("text", title || "Photography Session");
    
    if (startDate) {
      const start = formatGoogleDateTime(startDate, startTime || "09:00");
      const end = formatGoogleDateTime(startDate, endTime || "17:00");
      params.set("dates", `${start}/${end}`);
    }
    
    if (description) {
      params.set("details", description);
    }
    
    if (location) {
      params.set("location", location);
    }
    
    return `https://calendar.google.com/calendar/render?${params.toString()}`;
  };

  // Generate ICS file content for Apple Calendar / Outlook
  const generateIcsContent = () => {
    const formatIcsDateTime = (date, time) => {
      if (!date) return "";
      const dateStr = date.replace(/-/g, "");
      const timeStr = time ? time.replace(":", "") + "00" : "000000";
      return `${dateStr}T${timeStr}`;
    };

    const escapeIcs = (str) => {
      if (!str) return "";
      return str
        .replace(/\\/g, "\\\\")
        .replace(/;/g, "\\;")
        .replace(/,/g, "\\,")
        .replace(/\n/g, "\\n");
    };

    const uid = `${Date.now()}-${Math.random().toString(36).substr(2, 9)}@360lumina.com`;
    const now = new Date().toISOString().replace(/[-:]/g, "").split(".")[0] + "Z";
    
    const start = formatIcsDateTime(startDate, startTime || "09:00");
    const end = formatIcsDateTime(startDate, endTime || "17:00");

    const lines = [
      "BEGIN:VCALENDAR",
      "VERSION:2.0",
      "PRODID:-//360Lumina//Photography Booking//EN",
      "CALSCALE:GREGORIAN",
      "METHOD:PUBLISH",
      "BEGIN:VEVENT",
      `UID:${uid}`,
      `DTSTAMP:${now}`,
      `DTSTART:${start}`,
      `DTEND:${end}`,
      `SUMMARY:${escapeIcs(title || "Photography Session")}`,
    ];

    if (description) {
      lines.push(`DESCRIPTION:${escapeIcs(description)}`);
    }
    
    if (location) {
      lines.push(`LOCATION:${escapeIcs(location)}`);
    }

    lines.push(
      "STATUS:CONFIRMED",
      "BEGIN:VALARM",
      "TRIGGER:-PT1H",
      "ACTION:DISPLAY",
      "DESCRIPTION:Reminder",
      "END:VALARM",
      "BEGIN:VALARM",
      "TRIGGER:-P1D",
      "ACTION:DISPLAY", 
      "DESCRIPTION:Reminder",
      "END:VALARM",
      "END:VEVENT",
      "END:VCALENDAR"
    );

    return lines.join("\r\n");
  };

  // Download ICS file
  const downloadIcs = () => {
    setDownloading(true);
    try {
      const icsContent = generateIcsContent();
      const blob = new Blob([icsContent], { type: "text/calendar;charset=utf-8" });
      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = `${(title || "event").replace(/[^a-z0-9]/gi, "_").toLowerCase()}.ics`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
    } finally {
      setDownloading(false);
    }
  };

  // Open Google Calendar in new tab
  const openGoogleCalendar = () => {
    window.open(getGoogleCalendarUrl(), "_blank", "noopener,noreferrer");
  };

  // Generate Outlook.com URL (web)
  const getOutlookUrl = () => {
    const params = new URLSearchParams();
    params.set("path", "/calendar/action/compose");
    params.set("rru", "addevent");
    params.set("subject", title || "Photography Session");
    
    if (startDate && startTime) {
      const start = new Date(`${startDate}T${startTime}:00`);
      params.set("startdt", start.toISOString());
    }
    
    if (startDate && endTime) {
      const end = new Date(`${startDate}T${endTime}:00`);
      params.set("enddt", end.toISOString());
    }
    
    if (description) {
      params.set("body", description);
    }
    
    if (location) {
      params.set("location", location);
    }
    
    return `https://outlook.live.com/calendar/0/deeplink/compose?${params.toString()}`;
  };

  if (!startDate) {
    return null;
  }

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button 
          variant={variant} 
          size={size} 
          className={className}
          data-testid="add-to-calendar-btn"
        >
          <Calendar className="w-4 h-4 mr-1" />
          Add to Calendar
          <ChevronDown className="w-3 h-3 ml-1" />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-48">
        <DropdownMenuItem 
          onClick={openGoogleCalendar}
          className="cursor-pointer"
          data-testid="add-to-google-calendar"
        >
          <ExternalLink className="w-4 h-4 mr-2" />
          Google Calendar
        </DropdownMenuItem>
        <DropdownMenuItem 
          onClick={() => window.open(getOutlookUrl(), "_blank", "noopener,noreferrer")}
          className="cursor-pointer"
          data-testid="add-to-outlook"
        >
          <ExternalLink className="w-4 h-4 mr-2" />
          Outlook.com
        </DropdownMenuItem>
        <DropdownMenuItem 
          onClick={downloadIcs}
          className="cursor-pointer"
          disabled={downloading}
          data-testid="download-ics"
        >
          <Download className="w-4 h-4 mr-2" />
          {downloading ? "Downloading..." : "Download .ics"}
          <span className="text-xs text-muted-foreground ml-1">(Apple/Outlook)</span>
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
};

export default AddToCalendar;
